<script lang="ts" setup name="OrganisationPortCallsVoyageOverview">
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import { BASE_ITEM_TYPE } from '~/constants';
import {
  FUEL_TYPE,
  FUEL_TYPE_TITLES,
  PORT_CALLS_OVERVIEW_WIDGET_TYPE,
  PORT_CALLS_OVERVIEW_WIDGET_TYPES_TO_TITLE,
} from '~/constants/organisationPortCalls';

import { Item, useBaseItems } from '~/features/useBaseItem';
import { useService } from '~/features/useService.js';
import { isEmpty } from '~/features/useUtilities';

import Service from '~/services/Service';

import BaseItem from '~/components/BaseItem.vue';
import Card from '~/components/Card.vue';
import ServiceCard from '~/components/ServiceCard.vue';

import type {
  IPortCallsVesselLinkData,
  IPortCallsVoyageOverview,
  IPortCallsVoyageOverviewResponse,
} from '~/types';

const { organisationId, portId, voyageId } = defineProps<{
  organisationId: OrganisationId;
  voyageId: string;
  portId: string;
}>();

const router = useRouter();

const overview = reactive<IPortCallsVoyageOverview>({
  vessel: {
    id: '',
    name: '',
  },
  voyage: '',
  qtyOnBoard: 0,
  totalPortDays: 0,
  trade: '',
  fueling: [],
});

const voyageStatus = ref('');

const fuelingItems = computed(() => {
  const requiredFuelTypes = [FUEL_TYPE.VLSFO, FUEL_TYPE.LSMGO];
  const optionalFuelTypes = [FUEL_TYPE.BIO_VLSFO, FUEL_TYPE.BIO_LSMGO];

  const presentOptionalTypes = optionalFuelTypes.filter((type) =>
    overview.fueling?.some((fuelItem) => fuelItem.type === type),
  );

  const typesToRender = [...requiredFuelTypes, ...presentOptionalTypes];

  return useBaseItems(
    ...typesToRender.map((type) =>
      Item.label(FUEL_TYPE_TITLES[type])
        .value(
          overview.fueling?.find((fuelItem) => fuelItem.type === type)?.value,
        )
        .type('number')
        .fallback('-'),
    ),
  );
});

const items = computed(() => {
  const dataItems = Object.entries(overview)
    .map(([key, value]) => {
      if (
        key === PORT_CALLS_OVERVIEW_WIDGET_TYPE.STATUS ||
        key === PORT_CALLS_OVERVIEW_WIDGET_TYPE.FUELING
      )
        return null;

      const label = PORT_CALLS_OVERVIEW_WIDGET_TYPES_TO_TITLE[key] || key;
      const baseItem = Item.label(label)
        .value(
          key === 'vessel'
            ? (value as IPortCallsVesselLinkData)?.name
            : (value as string | number),
        )
        .fallback();

      if (
        key === PORT_CALLS_OVERVIEW_WIDGET_TYPE.QTY_ON_BOARD ||
        key === PORT_CALLS_OVERVIEW_WIDGET_TYPE.TOTAL_PORT_DAYS
      ) {
        baseItem.type('number');
      }

      if (key === PORT_CALLS_OVERVIEW_WIDGET_TYPE.QTY_ON_BOARD) {
        baseItem.unit('MT');
      }

      if (key === PORT_CALLS_OVERVIEW_WIDGET_TYPE.VESSEL) {
        const link = generateVesselLink(value as IPortCallsVesselLinkData);
        if (link) {
          baseItem.type('link').link(link);
        }
      }

      return baseItem;
    })
    .filter((item): item is Item => item !== null);

  return useBaseItems(...dataItems);
});

const generateVesselLink = (vessel: IPortCallsVesselLinkData) => {
  if (!vessel?.id || !vessel?.name) return;

  return router.resolve({
    name: 'OrganisationVesselsShow',
    params: {
      id: vessel.id,
      name: vessel.name,
    },
  }).href;
};

const serviceState = useService(
  Service.organisation(organisationId)
    .portCallsCalls(voyageId)
    .voyageOverview(portId),
  ({ data }: { data: IPortCallsVoyageOverviewResponse }) => {
    if (isEmpty(data)) {
      return false;
    }

    overview.vessel = data.vessel;
    overview.voyage = data.voyage;
    overview.qtyOnBoard = data.qtyOnBoard;
    overview.totalPortDays = data.totalPortDays;
    overview.trade = data.trade;
    overview.fueling = data.fueling ?? [];

    voyageStatus.value = data.status || '';
    return true;
  },
);
</script>

<template>
  <ServiceCard v-bind="serviceState" styled>
    <Card title="Voyage Overview" data-test="voyage-information" class="h-full">
      <div class="grid gap-4">
        <BaseItem
          tagVariant="contrast-green-1"
          label="Voyage Status"
          :value="voyageStatus"
          :type="BASE_ITEM_TYPE.TAG_VARIANT"
        />

        <div class="grid w-full grid-cols-2 gap-6">
          <BaseItem v-for="item in items" v-bind="item" :key="item.id" />

          <BaseItem
            v-for="fuelItem in fuelingItems"
            v-bind="fuelItem"
            :key="fuelItem.id"
          />
        </div>
      </div>
    </Card>
  </ServiceCard>
</template>
